import { buffer } from '@turf/turf';
import type { LineString, MultiLineString, MultiPolygon, Polygon } from 'geojson';
import { map } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useAsync } from 'react-use';
import { useDeepCompareMemo } from 'use-deep-compare';
import { useSnapshot } from 'valtio';
import { getCost } from '~/common/billingUtils';
import { createCircleFromPoint } from '~/common/geoUtils';
import { type CircleObject, EditorMode, type LineObject, ProjectGeometryType } from '~/common/types';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import { getCircleDestCoords } from '~/components2/Map/canvas/circle/common';
import { shouldConvertToMiles } from '~/components2/Map/canvas/line/common';
import { calculateArea, calculateLength } from '~/helpers/unitHelpers';
import { useValidateGeometry } from '~/hooks/useValidateGeometry';
import { homeMapStore as store } from '~/pages/homeViewV2/store';
import rootStore from '~/store';
import SegmentButtons from '../TopNav/SegmentButtons';
import NewProjectDetails from './NewProjectDetails';
import SelectProjectType from './SelectProjectType';
import UploadKMZProject from './UploadKMZProject';

export const exitProjectCreationMode = () => {
  store.setNewProjectMode(false);
  store.setInUploadKMZMode(false);
  store.setIsGeometryFromKMZ(false);
  store.setKmzFileName(undefined);
};

function setObjectColors(color: string) {
  store.shouldRedraw = true;
  store.objectsAsArray.forEach((o) => {
    store.updateObject({ id: o.id, properties: { color } });
  });
}

export default function NewProject() {
  const { editorMode, geometryType, objectsAsArray, inUploadKMZMode, geometryAsMultiPolygon } = useSnapshot(store);
  const { currentOrganization } = useSnapshot(rootStore);
  const onBack = exitProjectCreationMode;

  const validationResult = useValidateGeometry({
    geometry: geometryAsMultiPolygon,
  });

  useEffect(() => {
    if (validationResult.data?.isValid === true) {
      setObjectColors('orange');
    } else if (validationResult.data?.isValid === false) {
      setObjectColors('red');
    }
  }, [validationResult.data?.isValid]);

  useEffect(() => {
    return () => {
      store.setIsGeometryFromKMZ(false);
      store.setInUploadKMZMode(false);
      store.setKmzFileName(undefined);
    };
  }, []);

  const geomAsGeoJSON = useDeepCompareMemo<GeoJSON.MultiLineString | GeoJSON.MultiPolygon>(() => {
    if (geometryType === ProjectGeometryType.Linear) {
      return {
        type: 'MultiLineString',
        properties: { segmentNames: objectsAsArray.map((o) => o.title) },
        coordinates: map(objectsAsArray, (o) => (o.geometry as LineString).coordinates),
      };
    } else if (geometryType === ProjectGeometryType.Circle) {
      return {
        type: 'MultiLineString',
        properties: { segmentName: objectsAsArray.map((o) => o.title) },
        coordinates: map(objectsAsArray, (o) => {
          const c = o as CircleObject;
          const radius = c.properties.radius; // pass in as feet, fn handles conversion
          const coords = c.geometry.coordinates;
          const destCoords = getCircleDestCoords({
            lngLat: { lng: coords[0], lat: coords[1] },
            radius,
          }).geometry.coordinates;

          const center = c.geometry.coordinates;
          return [center, destCoords];
        }),
      };
    } else {
      return {
        type: 'MultiPolygon',
        properties: { segmentNames: objectsAsArray.map((o) => o.title) },
        coordinates: map(objectsAsArray, (o) => (o.geometry as Polygon).coordinates),
      };
    }
  }, [geometryType, objectsAsArray]);

  // calculate area and length
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const lengthAndArea = useMemo(() => {
    const zeroValue = {
      length: { unit: 'ft', value: 0.1 },
      area: { unit: 'ac', value: 0.1 },
    };
    if (geomAsGeoJSON.coordinates.length === 0) {
      return zeroValue;
    }
    if (geometryType === ProjectGeometryType.Linear) {
      const multiLine = geomAsGeoJSON as MultiLineString;
      let length = calculateLength({
        unitSystem: store.currentUnitSystem,
        geometry: multiLine,
      });
      if (shouldConvertToMiles({ store, line: length })) {
        length = calculateLength({
          unitSystem: store.currentUnitSystem,
          unitType: 'miles',
          geometry: multiLine,
        });
      }
      const area = calculateArea({
        unitSystem: store.currentUnitSystem,
        geometry: buffer(multiLine, (objectsAsArray[0] as LineObject).properties.buffer, {
          units: 'feet',
        })?.geometry as MultiPolygon,
      });
      return { length, area };
    } else if (geometryType === ProjectGeometryType.Circle) {
      // show area for circles, length doesn't really matter
      const mp: GeoJSON.MultiPolygon = {
        type: 'MultiPolygon',
        coordinates: map(
          objectsAsArray as CircleObject[],
          (o) =>
            createCircleFromPoint({
              center: o.geometry,
              radius: o.properties.radius,
            }).coordinates,
        ),
      };

      return {
        ...zeroValue,
        area: calculateArea({
          unitSystem: store.currentUnitSystem,
          geometry: mp,
        }),
      };
    } else if (geometryType === ProjectGeometryType.Polygon) {
      return {
        ...zeroValue,
        area: calculateArea({
          unitSystem: store.currentUnitSystem,
          geometry: geomAsGeoJSON as MultiPolygon,
        }),
      };
    }

    return zeroValue;
  }, [geomAsGeoJSON, geometryType]);

  const projectCost = useAsync(async () => {
    return await getCost({
      projectType: geometryType!,
      geometry: geomAsGeoJSON,
      //@ts-expect-error
      subscription: currentOrganization!.subscription,
    });
  }, [geometryType, geomAsGeoJSON]);

  const title = useMemo(() => {
    if (inUploadKMZMode) {
      return 'New Project From KMZ';
    } else if (objectsAsArray.length === 0) {
      if (geometryType) {
        return 'Draw Project Boundary';
      } else {
        return 'Select Project Type';
      }
    } else {
      if (geometryType === ProjectGeometryType.Linear) {
        return 'New Linear Project';
      } else if (geometryType === ProjectGeometryType.Polygon) {
        return 'New Polygon Project';
      } else {
        return 'New Circle Project';
      }
    }
  }, [objectsAsArray, geometryType, inUploadKMZMode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const content = useMemo(() => {
    if (inUploadKMZMode) {
      return <UploadKMZProject />;
    } else if (objectsAsArray.length === 0) {
      // not started drawing yet
      if (editorMode === EditorMode.None) {
        return <SelectProjectType />;
      } else {
        return (
          <div className="my-2 w-full space-y-2 self-end rounded border bg-gray-100 p-2 px-4 py-4 text-center text-base">
            <div className="text-sm font-noto">Click anywhere to begin drawing</div>
            {(editorMode === EditorMode.DrawLine || editorMode === EditorMode.DrawPolygon) && (
              <div className="text-sm font-noto">
                Press{' '}
                <code className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-semibold text-gray-800 dark:border-gray-500 dark:bg-gray-600 dark:text-gray-100">
                  Enter
                </code>{' '}
                or double click to end
              </div>
            )}
          </div>
        );
      }
    } else if (objectsAsArray.length > 0) {
      return (
        <NewProjectDetails projectCost={projectCost} geomAsGeoJSON={geomAsGeoJSON} lengthAndArea={lengthAndArea} />
      );
    }

    return null;
  }, [objectsAsArray, editorMode, inUploadKMZMode]);

  return (
    <>
      <div className="absolute right-[500px] z-20">
        <SegmentButtons />
      </div>
      <div
        className="flex h-full min-h-0 min-w-[500px] max-w-[500px] flex-col overflow-auto px-2 py-1"
        data-testid="new-project-panel"
      >
        <div className="mb-2 flex select-none items-center justify-between border-b pb-2 text-lg">
          <h2 className="ml-1" data-testid="new-project-panel-title">
            {title}
          </h2>

          <CloseIconButton
            onClick={exitProjectCreationMode}
            aria-label="Close new project"
            data-testid="new-project-close"
          />
        </div>
        <div className="flex flex-grow flex-col ">{content}</div>
      </div>
    </>
  );
}
